import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaTenCoordinatorV1Service } from '../service';
import { GetLocationsTCRBrandsIO } from '../types';

export const getGetLocationsTCRBrandsQueryKey = (req: GetLocationsTCRBrandsIO['input']) =>
  ['TenCoordinatorV1', 'GetLocationsTCRBrands', req] as const;
type QueryKey = ReturnType<typeof getGetLocationsTCRBrandsQueryKey>;

/**
 * A hook that returns a query for the `GetLocationsTCRBrands` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetLocationsTCRBrandsQuery = <E = unknown, D = GetLocationsTCRBrandsIO['output']>(
  req: GetLocationsTCRBrandsIO['input'],
  options?: LimitedSchemaQueryOptions<GetLocationsTCRBrandsIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetLocationsTCRBrandsIO['output'], E, D, QueryKey>({
    queryKey: getGetLocationsTCRBrandsQueryKey(req),
    queryFn: () => SchemaTenCoordinatorV1Service.GetLocationsTCRBrands(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetLocationsTCRBrands` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetLocationsTCRBrandsQueries = <E = unknown, D = GetLocationsTCRBrandsIO['output']>(
  argsArr: {
    req: GetLocationsTCRBrandsIO['input'];
    options?: LimitedSchemaQueryOptions<GetLocationsTCRBrandsIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetLocationsTCRBrandsQueryKey(req),
      queryFn: () => SchemaTenCoordinatorV1Service.GetLocationsTCRBrands(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetLocationsTCRBrandsQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetLocationsTCRBrands` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetLocationsTCRBrands = () => {
  const queryClient = useQueryClient();

  const fetchGetLocationsTCRBrands = async <E = unknown, D = GetLocationsTCRBrandsIO['output']>(
    req: GetLocationsTCRBrandsIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetLocationsTCRBrandsIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetLocationsTCRBrandsIO['output'], E, D, QueryKey>(
      getGetLocationsTCRBrandsQueryKey(req),
      () => SchemaTenCoordinatorV1Service.GetLocationsTCRBrands(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetLocationsTCRBrands;
};
